import { collection, addDoc, updateDoc, doc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase.config';
import { Capacitor } from '@capacitor/core';

// Web-compatible import - only load Capacitor plugins in mobile environment
let Preferences;
let Contacts;

// Check if we're in a mobile environment
const isMobile = typeof window !== 'undefined' && Capacitor.isNativePlatform();

// Development mode flag - set to false in production
const USE_MOCK_CONTACTS = process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_MOCK_CONTACTS === 'true';

if (isMobile) {
  // Only import Capacitor plugins on mobile
  try {
    Preferences = require('@capacitor/preferences').Preferences;
    Contacts = require('@capacitor-community/contacts').Contacts;
  } catch (error) {
    console.warn('Capacitor plugins not available:', error);
  }
}

class ContactSyncService {
  constructor() {
    this.isMobile = false;
    this.syncInProgress = false;
    this.lastSyncTime = null;
    this.init();
  }

  async init() {
    try {
      // Check if running on mobile device
      this.isMobile = isMobile;
      
      // Get last sync time
      if (this.isMobile && Preferences) {
        const lastSync = await Preferences.get({ key: 'contact_last_sync' });
        this.lastSyncTime = lastSync.value ? parseInt(lastSync.value) : null;
      } else {
        // Use localStorage for web
        const lastSync = localStorage.getItem('acctoo_contact_last_sync');
        this.lastSyncTime = lastSync ? parseInt(lastSync) : null;
      }
      
      console.log('ContactSyncService initialized:', { isMobile: this.isMobile, lastSyncTime: this.lastSyncTime });
    } catch (error) {
      console.error('Error initializing ContactSyncService:', error);
    }
  }

  // Check if contacts permission is granted
  async checkPermission() {
    if (!this.isMobile) {
      return { granted: false, message: 'Contact sync is only available on mobile devices' };
    }

    if (USE_MOCK_CONTACTS) {
      return { granted: true, message: 'Mock contacts enabled for development' };
    }

    try {
      if (!Contacts) {
        return { granted: false, message: 'Contacts plugin not available' };
      }

      const permission = await Contacts.checkPermissions();
      const granted = permission.contacts === 'granted';
      
      return {
        granted,
        message: granted ? 'Permission granted' : 'Permission not granted'
      };
    } catch (error) {
      console.error('Error checking contacts permission:', error);
      return { granted: false, message: `Error checking permission: ${error.message}` };
    }
  }

  // Request contacts permission
  async requestPermission() {
    if (!this.isMobile) {
      return { granted: false, message: 'Contact sync is only available on mobile devices' };
    }

    if (USE_MOCK_CONTACTS) {
      return { granted: true, message: 'Mock contacts enabled for development' };
    }

    try {
      if (!Contacts) {
        return { granted: false, message: 'Contacts plugin not available' };
      }

      const permission = await Contacts.requestPermissions();
      const granted = permission.contacts === 'granted';
      
      return {
        granted,
        message: granted 
          ? 'Permission granted' 
          : 'Permission denied. Please enable contacts access in your device settings.'
      };
    } catch (error) {
      console.error('Error requesting contacts permission:', error);
      return { granted: false, message: `Error requesting permission: ${error.message}` };
    }
  }

  // Get all contacts from device
  async getDeviceContacts() {
    if (!this.isMobile) {
      return { success: false, contacts: [], message: 'Contact sync is only available on mobile devices' };
    }

    try {
      const permission = await this.checkPermission();
      if (!permission.granted) {
        return { success: false, contacts: [], message: 'Contacts permission not granted' };
      }

      // Use mock contacts in development mode
      if (USE_MOCK_CONTACTS || !Contacts) {
        const mockContacts = [
          {
            id: 'mock-1',
            name: 'John Doe',
            phones: [{ number: '+91 98765 43210' }],
            emails: [{ address: 'john@example.com' }],
            organization: 'ABC Company',
            syncedAt: new Date().toISOString()
          },
          {
            id: 'mock-2',
            name: 'Jane Smith',
            phones: [{ number: '+91 98765 43211' }],
            emails: [{ address: 'jane@example.com' }],
            organization: 'XYZ Corp',
            syncedAt: new Date().toISOString()
          },
          {
            id: 'mock-3',
            name: 'Mike Johnson',
            phones: [{ number: '+91 98765 43212' }],
            emails: [{ address: 'mike@example.com' }],
            organization: 'Tech Solutions',
            syncedAt: new Date().toISOString()
          }
        ];
        return { 
          success: true, 
          contacts: mockContacts, 
          message: `Found ${mockContacts.length} contacts (mock data for development)` 
        };
      }

      // Get real contacts from device (limited fields only)
      console.log('🔄 Fetching real device contacts...');
      const result = await Contacts.getContacts({
        projection: {
          name: true,
          phones: true,
          emails: true,
          organization: true
        }
      });

      if (!result || !result.contacts) {
        return { success: false, contacts: [], message: 'No contacts found on device' };
      }

      // Transform contacts to our format (limit fields)
      const transformedContacts = result.contacts.map(contact => ({
        id: contact.contactId,
        name: contact.name?.display || 'Unknown',
        phones: (contact.phones || []).map(p => ({ number: p.number })),
        emails: (contact.emails || []).map(e => ({ address: e.address })),
        organization: contact.organization?.company || null,
        syncedAt: new Date().toISOString()
      }));

      console.log(`✅ Fetched ${transformedContacts.length} real contacts from device`);
      
      return { 
        success: true, 
        contacts: transformedContacts, 
        message: `Found ${transformedContacts.length} contacts` 
      };
    } catch (error) {
      console.error('Error getting device contacts:', error);
      return { 
        success: false, 
        contacts: [], 
        message: `Error fetching contacts: ${error.message}` 
      };
    }
  }

  // Sync contacts to database
  async syncContactsToDatabase(userId, appId) {
    if (this.syncInProgress) {
      return { success: false, message: 'Sync already in progress' };
    }

    this.syncInProgress = true;

    try {
      // Get device contacts
      const { success, contacts, message } = await this.getDeviceContacts();
      
      if (!success) {
        return { success: false, message };
      }

      if (contacts.length === 0) {
        return { success: true, message: 'No contacts to sync', syncedCount: 0 };
      }

      // Store contacts in database
      const syncedContacts = [];
      const errors = [];

      for (const contact of contacts) {
        try {
          // Check if contact already exists
          const existingQuery = query(
            collection(db, `apps/${appId}/synced_contacts`),
            where('deviceContactId', '==', contact.id),
            where('userId', '==', userId)
          );
          
          const existingDocs = await getDocs(existingQuery);
          
          if (existingDocs.empty) {
            // Add new contact
            const docRef = await addDoc(collection(db, `apps/${appId}/synced_contacts`), {
              ...contact,
              userId,
              deviceContactId: contact.id,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            syncedContacts.push({ id: docRef.id, ...contact });
          } else {
            // Update existing contact
            const existingDoc = existingDocs.docs[0];
            await updateDoc(doc(db, `apps/${appId}/synced_contacts`, existingDoc.id), {
              ...contact,
              updatedAt: new Date().toISOString()
            });
            syncedContacts.push({ id: existingDoc.id, ...contact });
          }
        } catch (error) {
          console.error(`Error syncing contact ${contact.name}:`, error);
          errors.push({ contact: contact.name, error: error.message });
        }
      }

      // Update last sync time
      this.lastSyncTime = Date.now();
      if (this.isMobile && Preferences) {
        await Preferences.set({
          key: 'contact_last_sync',
          value: this.lastSyncTime.toString()
        });
      } else {
        // Use localStorage for web
        localStorage.setItem('acctoo_contact_last_sync', this.lastSyncTime.toString());
      }

      this.syncInProgress = false;

      return {
        success: true,
        message: `Synced ${syncedContacts.length} contacts successfully`,
        syncedCount: syncedContacts.length,
        errors: errors.length > 0 ? errors : null
      };

    } catch (error) {
      console.error('Error syncing contacts to database:', error);
      this.syncInProgress = false;
      return { success: false, message: 'Error syncing contacts to database' };
    }
  }

  // Get synced contacts from database
  async getSyncedContacts(appId, userId) {
    try {
      const contactsQuery = query(
        collection(db, `apps/${appId}/synced_contacts`),
        where('userId', '==', userId),
        orderBy('name', 'asc')
      );

      const querySnapshot = await getDocs(contactsQuery);
      const contacts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, contacts, message: `Found ${contacts.length} synced contacts` };
    } catch (error) {
      console.error('Error getting synced contacts:', error);
      return { success: false, contacts: [], message: 'Error fetching synced contacts' };
    }
  }

  // Search contacts by name or number
  async searchContacts(appId, userId, searchTerm) {
    try {
      const { success, contacts } = await this.getSyncedContacts(appId, userId);
      
      if (!success) {
        return { success: false, contacts: [], message: 'Error fetching contacts' };
      }

      const searchLower = searchTerm.toLowerCase();
      const filteredContacts = contacts.filter(contact => {
        const nameMatch = contact.name.toLowerCase().includes(searchLower);
        const phoneMatch = contact.phones.some(phone => 
          phone.number.replace(/\D/g, '').includes(searchTerm.replace(/\D/g, ''))
        );
        const emailMatch = contact.emails.some(email => 
          email.address.toLowerCase().includes(searchLower)
        );
        
        return nameMatch || phoneMatch || emailMatch;
      });

      return {
        success: true,
        contacts: filteredContacts,
        message: `Found ${filteredContacts.length} matching contacts`
      };
    } catch (error) {
      console.error('Error searching contacts:', error);
      return { success: false, contacts: [], message: 'Error searching contacts' };
    }
  }

  // Get contact by ID
  async getContactById(appId, userId, contactId) {
    try {
      const { success, contacts } = await this.getSyncedContacts(appId, userId);
      
      if (!success) {
        return { success: false, contact: null, message: 'Error fetching contacts' };
      }

      const contact = contacts.find(c => c.id === contactId);
      
      return {
        success: true,
        contact,
        message: contact ? 'Contact found' : 'Contact not found'
      };
    } catch (error) {
      console.error('Error getting contact by ID:', error);
      return { success: false, contact: null, message: 'Error fetching contact' };
    }
  }

  // Get sync status
  async getSyncStatus() {
    return {
      isMobile: this.isMobile,
      syncInProgress: this.syncInProgress,
      lastSyncTime: this.lastSyncTime,
      lastSyncDate: this.lastSyncTime ? new Date(this.lastSyncTime).toLocaleString() : null
    };
  }

  // Clear synced contacts
  async clearSyncedContacts(appId, userId) {
    try {
      // This would require additional implementation to delete all synced contacts
      // For now, just clear the last sync time
      if (this.isMobile && Preferences) {
        await Preferences.remove({ key: 'contact_last_sync' });
      } else {
        // Use localStorage for web
        localStorage.removeItem('acctoo_contact_last_sync');
      }
      this.lastSyncTime = null;
      
      return { success: true, message: 'Sync data cleared' };
    } catch (error) {
      console.error('Error clearing synced contacts:', error);
      return { success: false, message: 'Error clearing sync data' };
    }
  }
}

// Create singleton instance
const contactSyncService = new ContactSyncService();

export default contactSyncService;
